import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type KeyboardEvent } from 'react';

import type { Organization } from '@/api/organization';
import LockIcon from '@/assets/svg/lock.svg?react';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
} from '@/components/Form/pickers';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { useMe } from '@/providers/User';

type Props = {
  organizations?: Organization[];
  isLoading: boolean;
  error: Error | null;
  onSelectInstanceChange: (instanceId: string) => void;
  onClose: () => void;
};

const InstanceSwitcherMenu = ({
  organizations,
  isLoading,
  error,
  onSelectInstanceChange,
  onClose,
}: Props) => {
  const user = useMe();
  const currentInstanceId = user?.currentOrg.instanceId;

  const selectedOrganization = organizations?.find((org) => org.id === currentInstanceId) ?? null;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Combobox
      size="large"
      autoHighlight
      inline
      items={organizations ?? []}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, value) => item.id === value.id}
      value={selectedOrganization}
      isLoading={isLoading}
      error={error}
      onValueChange={(organization) => organization && onSelectInstanceChange(organization.id)}
    >
      <ComboboxInput
        autoFocus
        showTrigger={false}
        className="w-full"
        placeholder={t`Search`}
        onKeyDown={handleInputKeyDown}
      />
      <div className="max-h-64 overflow-y-auto">
        <ComboboxStatus />
        <ComboboxEmpty />
        <ComboboxList className="p-0">
          {(organization: Organization) => (
            <ComboboxItem key={organization.id} value={organization}>
              <div className="flex w-full items-center gap-1.5 pr-6">
                <span className="truncate">{organization.name ?? ''}</span>
                {!organization.isAuthenticated && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <span className="inline-flex shrink-0 items-center justify-center">
                          <LockIcon className="size-4! text-gray-600" />
                        </span>
                      }
                    />
                    <TooltipContent side="top">
                      <Trans>This instance requires a different sign-in method</Trans>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </ComboboxItem>
          )}
        </ComboboxList>
      </div>
    </Combobox>
  );
};

export default InstanceSwitcherMenu;
